import {
  ContextSelector,
  type ForecastContextSelectedOptions
} from "@appretio/context-selector";
import { ContextSyncWarning, formatDataAsOf } from "@appretio/shared";
import {
  Button,
  Dialog,
  Divider,
  ExpandSheetButton,
  IconButton,
  LoadingOverlay,
  Note,
  Sheet,
  Toast
} from "@appretio/ui-kit";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { AlgorithmicForecastDialog } from "../components/algorithmicForecastDialog/AlgorithmicForecastDialog";
import { ApplyButton } from "../components/applyButton/ApplyButton";
import { useCopyMenu } from "../components/cubeDataTable/copyToOwnRows/useCopyMenu";
import { ForecastDataTable } from "../components/cubeDataTable/ForecastDataTable";
import {
  isEditableIntakeSource,
  shownIntakeSources
} from "../components/cubeDataTable/utils/intakeUtils";
import { ForecastGraph } from "../components/cubeGraph/ForecastGraph";
import { EventCalendar } from "../components/eventCalendar/EventCalendar";
import { ForecastContextSelector } from "../components/forecastContextSelector/ForecastContextSelector";
import { StoreStatusFilter } from "../components/storeStatusFilter/StoreStatusFilter";
import { ForecastTableControls } from "../components/tableControls/ForecastTableControls";
import { isEditableCalendarView } from "../components/tableControls/forecastTableControlValues";
import { ContextViewSelector } from "../contextViews/ContextViewSelector";
import { FORECAST_CONTEXT_FIELDS } from "../forecastContext";
import {
  type MainPageState,
  useMainPageState,
  type UseMainPageStateParams
} from "../hooks/useMainPageState/index";
import {
  contextActionsContainer,
  contextContainer,
  contextDimensions,
  contextMessage,
  contextRow,
  contextSelectorsContainer,
  contextVersionActionsContainer,
  dataTableWrapper,
  emptyState,
  graphStack,
  tableActionButtons,
  tableControlsContainer,
  temporalGridBlock,
  temporalGridContent
} from "../styles/pageLayout.css";

/** Props for the shared temporal-grid page. */
export interface TemporalGridPageProps extends UseMainPageStateParams {
  /**
   * Render the version-selector trigger actions (icons nested inside the
   * version dropdown trigger). Receives the full page state so it can
   * read ``selectedVersionOptions``, ``isFrozen``, etc. For forecast
   * pages this is the delete button; for budget it's freeze + delete.
   */
  renderTriggerActions: (state: MainPageState) => ReactNode;
  /**
   * Render the footer shown inside the version dropdown list (typically
   * a "New Version" button). Receives the full page state, a ``close``
   * callback that closes the dropdown, and an ``openModal`` callback that
   * opens the associated modal rendered via ``renderModal``.
   */
  renderFooter: (
    state: MainPageState,
    close: () => void,
    openModal: () => void
  ) => ReactNode;
  /**
   * Render any modals triggered from the footer. Rendered as a sibling to
   * the dropdown (outside the popover) so they stay mounted when it closes.
   * Receives the page state, a boolean indicating whether the modal is open,
   * and a setter to control it.
   */
  renderModal?: (
    state: MainPageState,
    modalOpen: boolean,
    setModalOpen: (open: boolean) => void
  ) => ReactNode;
  /**
   * Optional banner rendered above the data table (used for the budget
   * frozen-cube notice). Returns ``null`` when not applicable.
   */
  renderFrozenBanner?: (state: MainPageState) => ReactNode;
}

/**
 * Why the Load-forecast entry is disabled, or `undefined` when it is open.
 * Loading is read-only, so unlike Save it needs no save target and tolerates
 * a frozen version. It does need a loaded grid on the Inv 454 view, the only
 * view the algorithmic producer is rendered into (SPEC § Result rows).
 */
function loadForecastBlocker(state: MainPageState): string | undefined {
  if (!state.workspace.rowData.length) {
    return "Load forecast: apply a version first";
  }
  if (state.workspace.tableControls.calendarView !== "Inv 454") {
    return "Load forecast: switch to the Inv 454 view";
  }
  if (state.workspace.isFetching) return "Load forecast: the grid is loading";
  return undefined;
}

/**
 * Why the save cube's rows are locked for this context, or `undefined` when
 * they are not. Only shown on an editable grid: elsewhere every row is locked.
 */
function cubeHoldsNote(state: MainPageState): string | undefined {
  const data = state.workspace.forecastData;
  if (!data || !state.editableProducer) return undefined;
  const locked = [
    !data.save_cube_holds_sales && "sales",
    !data.save_cube_holds_intake && "intake"
  ].filter(Boolean);
  if (!locked.length) return undefined;
  return `This version holds nothing for the selected context, so its ${locked.join(" and ")} can't be edited.`;
}

/**
 * Shared layout for the forecast and budget temporal grid pages.
 *
 * Owns all the duplicated chrome: context selector + version selector,
 * Apply/Save buttons, table controls, graph/event-calendar/data-table
 * stack, restate-FPOS confirm dialog, and the save-success Toast.
 * Producer-specific delete/create/freeze buttons (which differ in
 * shape and prop requirements between the two pages) are injected via
 * the ``renderTriggerActions`` / ``renderFooter`` slots.
 *
 * The shared component routes the rendered slots to the correct
 * ``ForecastContextSelector`` props based on ``producer``:
 * ``userVersion*`` for the forecast page, ``budgetVersion*`` for the
 * budget page.
 */
export function TemporalGridPage({
  producer,
  saveCubeKey,
  renderTriggerActions,
  renderFooter,
  renderModal,
  renderFrozenBanner
}: TemporalGridPageProps) {
  const state = useMainPageState({ producer, saveCubeKey });
  const getContextMenuItems = useCopyMenu(state);
  // A fresh object per render would defeat the `React.memo` on the grid.
  const gridOptions = useMemo(
    () => ({ getContextMenuItems }),
    [getContextMenuItems]
  );
  // The grid gates intake edits on the source it shows, not on the control:
  // after a failed swap the control stays on Planned while the grid rolls
  // back, and the save sends only the source on the grid.
  const { tableControls, gridSource } = state.workspace;
  const gridControls = useMemo(
    () => ({ ...tableControls, intake: gridSource }),
    [tableControls, gridSource]
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [algoForecastDialogOpen, setAlgoForecastDialogOpen] = useState(false);
  const triggerActions = renderTriggerActions(state);
  const modal = renderModal?.(state, modalOpen, setModalOpen);
  const frozenBanner = renderFrozenBanner?.(state);
  const holdsNote = cubeHoldsNote(state);
  const isBudget = producer === "budget";
  const loadForecastBlocked = loadForecastBlocker(state);
  // The fiscal years the loaded grid shows — read from the fetched payload
  // (not the live AG Grid rows) so later client-side merges can't feed the
  // algorithmic-forecast week pickers.
  const gridFiscalYears = useMemo(
    () =>
      Array.from(
        new Set(
          (state.workspace.forecastData?.fiscal_grid_data ?? []).map(
            (row) => row.fiscal_year_name
          )
        )
      ),
    [state.workspace.forecastData]
  );

  return (
    <>
      <Sheet>
        <div className={contextContainer}>
          <div className={contextActionsContainer}>
            <ApplyButton
              onApply={state.workspace.handleApply}
              hasUnsavedEdits={state.workspace.hasUnsavedEdits}
              onSaveFirst={
                state.canSaveFirst ? state.workspace.saveGrid : undefined
              }
              disabled={!state.canApply}
              isLoading={state.workspace.isFetching}
            />
            <Button
              variant="secondary"
              size="xs"
              onClick={state.workspace.handleSave}
              disabled={!state.canSave}
              isLoading={state.workspace.isSaving}
            >
              Save
            </Button>
          </div>
          <div className={contextSelectorsContainer}>
            <div className={contextRow}>
              <div className={contextDimensions}>
                <ContextSelector
                  fields={FORECAST_CONTEXT_FIELDS}
                  selected={state.selectedContextOptions}
                  setSelected={
                    state.setSelectedContextOptions as React.Dispatch<
                      React.SetStateAction<ForecastContextSelectedOptions>
                    >
                  }
                  baseContextOptions={state.baseContextOptions}
                  filteredContextOptions={
                    state.hasActiveFilters ||
                    state.selectedStoreStatuses.length > 0 ||
                    state.cubeIds.length > 0
                      ? state.mergedFilteredContextOptions
                      : undefined
                  }
                  isLoading={state.baseLoading || state.filteredLoading}
                />
              </div>
              <ContextViewSelector views={state.contextViews} />
            </div>

            <div className={contextVersionActionsContainer}>
              <ForecastContextSelector
                selected={state.selectedVersionOptions}
                setSelected={state.setSelectedVersionOptions}
                brandSelected={state.selectedBrand}
                error={
                  state.cubeIds.length === 0 && state.workspace.hasVersionError
                }
                userVersionTriggerActions={
                  !isBudget ? triggerActions : undefined
                }
                userVersionFooter={
                  !isBudget
                    ? (close) =>
                        renderFooter(state, close, () => setModalOpen(true))
                    : undefined
                }
                budgetVersionTriggerActions={
                  isBudget ? triggerActions : undefined
                }
                budgetVersionFooter={
                  isBudget
                    ? (close) =>
                        renderFooter(state, close, () => setModalOpen(true))
                    : undefined
                }
              />
              <StoreStatusFilter
                options={state.storeStatusOptions ?? []}
                selected={state.selectedStoreStatuses}
                onChange={state.setSelectedStoreStatuses}
                isLoading={state.storeStatusLoading}
                relevantStatuses={state.relevantStoreStatuses}
              />
              {state.workspace.forecastData && !state.workspace.isFetching && (
                <div className={contextMessage}>
                  Data Loaded @{" "}
                  {formatDataAsOf(state.workspace.forecastData.loaded_at)}
                  {state.workspace.savedAt &&
                    ` · Saved @ ${formatDataAsOf(state.workspace.savedAt)}`}
                </div>
              )}
            </div>
          </div>
        </div>
      </Sheet>

      <Sheet
        overlay={
          state.workspace.isFetching ? (
            <LoadingOverlay aria-label="Loading forecast data" />
          ) : undefined
        }
      >
        <div className={temporalGridContent}>
          <div className={graphStack}>
            {state.graphVisible && (
              <ForecastGraph
                selectedRows={state.selectedRows}
                timePeriod={state.workspace.tableControls.timePeriod}
                weekLabels={state.workspace.tableControls.weekLabels}
                accumulated={state.accumulated}
                onAccumulatedChange={state.setAccumulated}
                context={state.workspace.appliedFilters.context}
                resolveCubeId={state.resolveCubeId}
                metaData={state.workspace.metaData}
                isMetaDataFetching={state.workspace.isMetaDataFetching}
              />
            )}
            {state.workspace.tableControls.calendarView &&
              state.eventCalendarVisible && (
                <EventCalendar
                  metaData={state.workspace.metaData}
                  timePeriod={state.workspace.tableControls.timePeriod}
                  weekLabels={state.workspace.tableControls.weekLabels}
                />
              )}
          </div>
          <div className={temporalGridBlock}>
            {frozenBanner}
            {state.workspace.forecastData && state.isContextDirty && (
              <ContextSyncWarning changedFields={state.changedFields} />
            )}
            {holdsNote && (
              <Note variant="info" size="compact" text={holdsNote} />
            )}
            {state.workspace.algoFailures.length > 0 && (
              <Note
                variant="warning"
                size="compact"
                text={`Not loaded — ${state.workspace.algoFailures.join("; ")}`}
              />
            )}
            <div className={tableControlsContainer}>
              <ForecastTableControls
                options={state.workspace.tableControlOptions}
                setOptions={state.workspace.setTableControlOptions}
                values={state.workspace.tableControls}
                setValues={state.workspace.setTableControls}
                hasIntakeData={state.workspace.hasIntakeData}
                availableIntakeSources={state.workspace.availableIntakeSources}
                intakeSources={shownIntakeSources(isBudget)}
                intakeLoading={state.workspace.intakePending}
                views={state.views}
              />
              <div className={tableActionButtons}>
                <IconButton
                  iconName="graph"
                  variant="tertiary"
                  size="sm"
                  title="Toggle graph"
                  aria-label={state.graphVisible ? "Hide graph" : "Show graph"}
                  onClick={() => state.setGraphVisible((v) => !v)}
                />
                {state.workspace.tableControls.calendarView && (
                  <IconButton
                    iconName="calendar"
                    variant="tertiary"
                    size="sm"
                    title="Toggle event calendar"
                    aria-label={
                      state.eventCalendarVisible
                        ? "Hide event calendar"
                        : "Show event calendar"
                    }
                    onClick={() => state.setEventCalendarVisible((v) => !v)}
                  />
                )}
                <IconButton
                  iconName="wand"
                  variant="tertiary"
                  size="sm"
                  title="Auto-populate intake"
                  aria-label="Auto-populate intake"
                  disabled={
                    !state.workspace.rowData.length ||
                    !isEditableIntakeSource(gridControls.intake) ||
                    !isEditableCalendarView(
                      state.workspace.tableControls.calendarView
                    ) ||
                    state.workspace.fillInOtbPending ||
                    state.isGridFrozen
                  }
                  isLoading={state.workspace.fillInOtbPending}
                  onClick={state.workspace.handleFillInOtb}
                />
                <IconButton
                  iconName="radiation"
                  variant="tertiary"
                  size="sm"
                  title="Re-state FPOS to default values"
                  aria-label="Re-state FPOS to default values"
                  disabled={!state.canSave || !state.workspace.rowData.length}
                  onClick={() => state.workspace.setRestateFPOSDialogOpen(true)}
                />
                <Divider orientation="vertical" />
                <IconButton
                  iconName="trend-up"
                  variant="tertiary"
                  size="sm"
                  aria-label="Load forecast"
                  tooltip={loadForecastBlocked ?? "Load forecast"}
                  disabled={loadForecastBlocked !== undefined}
                  onClick={() => setAlgoForecastDialogOpen(true)}
                />
                <ExpandSheetButton variant="tertiary" size="sm" />
              </div>
            </div>
            {!state.workspace.forecastData && !state.workspace.isFetching ? (
              <div className={emptyState}>
                Select a version and click Apply to load forecast data.
              </div>
            ) : (
              <div className={dataTableWrapper}>
                <ForecastDataTable
                  ref={state.gridRef}
                  tableControls={gridControls}
                  viewState={state.views.resolvedViewState}
                  rowData={state.workspace.rowData}
                  editableProducer={state.editableProducer}
                  cubeWindow={state.gridCubeWindow}
                  editableRowPredicate={state.gridEditableRowPredicate}
                  gridStateRetention={state.workspace.gridStateRetention}
                  pendingViewColumnState={state.views.pendingColumnState}
                  onViewColumnStateApplied={state.views.clearPendingColumnState}
                  metaData={state.workspace.metaData}
                  isMetaDataFetching={state.workspace.isMetaDataFetching}
                  onCellValueChanged={
                    state.editableProducer
                      ? state.handleCellValueChanged
                      : undefined
                  }
                  onSelectionChanged={state.handleSelectionChanged}
                  onRowDataUpdated={state.handleRowDataUpdated}
                  onPasteStart={
                    state.editableProducer ? state.handleBatchStart : undefined
                  }
                  onPasteEnd={
                    state.editableProducer ? state.handleBatchEnd : undefined
                  }
                  onCellSelectionDeleteStart={
                    state.editableProducer ? state.handleBatchStart : undefined
                  }
                  onCellSelectionDeleteEnd={
                    state.editableProducer ? state.handleBatchEnd : undefined
                  }
                  gridOptions={gridOptions}
                />
                {state.workspace.intakePending &&
                  !state.workspace.isFetching && (
                    <LoadingOverlay aria-label="Recalculating intake" />
                  )}
              </div>
            )}
          </div>
        </div>
      </Sheet>

      <Dialog
        hideFeedbackIcon
        open={state.workspace.restateFPOSDialogOpen}
        title="Reset Target Stock"
        onOpenChange={state.workspace.setRestateFPOSDialogOpen}
        confirmText="Yes, reset"
        cancelText="No"
        onConfirm={state.workspace.handleRestateFPOS}
        isLoading={state.workspace.restateFPOSPending}
      >
        <p>
          Reset Target Stock to the default FPOS values? This overwrites every
          FPOS and Target Stock edit in this cube, saved or not, and reloads the
          grid, which discards any other unsaved change. Save first to keep
          them. This cannot be undone.
        </p>
      </Dialog>

      <Toast
        key={state.workspace.toastState.raised}
        open={state.workspace.toastState.open}
        setOpen={state.workspace.setToastOpen}
        variant={state.workspace.toastState.variant}
        duration={state.workspace.toastState.duration}
        title={state.workspace.toastState.title}
        description={state.workspace.toastState.message}
      />
      <AlgorithmicForecastDialog
        open={algoForecastDialogOpen}
        onOpenChange={setAlgoForecastDialogOpen}
        context={state.workspace.appliedFilters.context}
        editableWeekFrom={state.workspace.forecastData?.editable_week_from}
        forecastWeekFrom={state.workspace.forecastData?.forecast_week_from}
        fiscalYears={gridFiscalYears}
        weekLabels={state.workspace.tableControls.weekLabels}
        metaData={state.workspace.metaData}
        onLoaded={state.workspace.addAlgoForecast}
      />
      {modal}
    </>
  );
}
